import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { enviarArquivo } from '../lib/quadro';
import {
  buscarAlbuns,
  buscarEventosParaAlbum,
  buscarMidias,
  incorporarVideo,
  publicarMidia,
  removerMidia,
  tamanhoLegivel,
  type Album,
  type Midia,
} from '../lib/midias';
import { Alerta, Botao, Cartao } from './Interface';
import { useConfirmacao } from './Confirmacao';
import Layout from './Layout';

const dataCurta = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(iso))
    : '';

type Props = {
  tipo: 'foto' | 'video';
  titulo: string;
  subtitulo: string;
  vazio: string;
};

/** Uma galeria por tipo: as palavras em video e as fotos vivem separadas. */
export default function Galeria({ tipo, titulo, subtitulo, vazio }: Props) {
  const [albuns, setAlbuns] = useState<Album[]>([]);
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [midias, setMidias] = useState<Midia[]>([]);
  const [podePublicar, setPodePublicar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [abrindo, setAbrindo] = useState<Midia | null>(null);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        buscarAlbuns(tipo),
        buscarMidias(eventoId ?? undefined, tipo),
      ]);
      setAlbuns(a.albuns);
      setMidias(m.midias);
      setPodePublicar(m.podePublicar);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar.');
    }
  }, [tipo, eventoId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function apagar(m: Midia) {
    const ok = await confirmar({
      titulo: tipo === 'video' ? 'Tirar esta palavra do ar?' : 'Remover esta foto?',
      texto: 'O item sai da área de membros. O arquivo continua guardado no servidor.',
      confirmar: 'Remover',
    });
    if (!ok) return;
    try {
      await removerMidia(m.id);
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui remover.');
    }
  }

  return (
    <Layout
      titulo={titulo}
      subtitulo={subtitulo}
      acoes={
        podePublicar && (
          <Botao onClick={() => setPublicando(true)}>
            {tipo === 'video' ? '+ Nova palavra' : '+ Novo album'}
          </Botao>
        )
      }
    >
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {aviso && (
        <div className="mb-4 max-w-2xl">
          <Alerta tipo="ok">{aviso}</Alerta>
        </div>
      )}

      {albuns.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={() => setEventoId(null)}
            className={`rounded-xl px-3.5 py-2 text-sm font-medium transition ${
              eventoId === null
                ? 'bg-tinta-900 text-white'
                : 'bg-white text-tinta-600 shadow-cartao hover:bg-tinta-100'
            }`}
          >
            Todos
          </button>
          {albuns.map((a) => (
            <button
              key={a.eventoId}
              onClick={() => setEventoId(a.eventoId)}
              className={`rounded-xl px-3.5 py-2 text-sm font-medium transition ${
                eventoId === a.eventoId
                  ? 'bg-tinta-900 text-white'
                  : 'bg-white text-tinta-600 shadow-cartao hover:bg-tinta-100'
              }`}
            >
              {a.titulo}
              <span className="ml-2 text-xs opacity-70">
                {dataCurta(a.inicio)} · {tipo === 'video' ? a.videos : a.fotos}
              </span>
            </button>
          ))}
        </div>
      )}

      {midias.length === 0 && (
        <Cartao>
          <p className="text-sm text-tinta-400">
            {vazio}
            {podePublicar
              ? tipo === 'video'
                ? ' Use "+ Nova palavra" ali em cima.'
                : ' Use "+ Enviar fotos" ali em cima.'
              : ''}
          </p>
        </Cartao>
      )}

      {tipo === 'video' && midias.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {midias.map((v) => {
            const incorporado = v.link ? incorporarVideo(v.link) : null;
            return (
              <Cartao key={v.id} className="p-0">
                {incorporado ? (
                  <iframe
                    src={incorporado}
                    title={v.titulo ?? 'Palavra'}
                    allowFullScreen
                    className="aspect-vídeo w-full rounded-t-2xl"
                  />
                ) : v.caminho ? (
                  <video
                    controls
                    preload="metadata"
                    src={`/api/uploads/${v.caminho}`}
                    className="aspect-vídeo w-full rounded-t-2xl bg-black"
                  />
                ) : (
                  <a
                    href={v.link ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex aspect-vídeo items-center justify-center rounded-t-2xl bg-tinta-900 text-white"
                  >
                    Abrir video
                  </a>
                )}
                <div className="p-4">
                  <p className="font-medium text-tinta-900">{v.titulo ?? 'Palavra'}</p>
                  <p className="text-xs text-tinta-400">
                    {v.eventoTitulo ? `${v.eventoTitulo} · ` : ''}
                    {dataCurta(v.eventoInicio ?? v.criadoEm)}
                  </p>
                  {v.descricao && <p className="mt-2 text-sm text-tinta-600">{v.descricao}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    {v.caminho && (
                      <a
                        href={`/api/uploads/${v.caminho}`}
                        download
                        className="font-medium text-tinta-900 hover:underline"
                      >
                        ↓ Baixar {tamanhoLegivel(v.bytes)}
                      </a>
                    )}
                    {v.link && !v.caminho && (
                      <a
                        href={v.link}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="font-medium text-tinta-900 hover:underline"
                      >
                        Abrir no YouTube
                      </a>
                    )}
                    {podePublicar && (
                      <button
                        onClick={() => void apagar(v)}
                        className="ml-auto text-xs text-tinta-400 hover:text-red-600"
                      >
                        remover
                      </button>
                    )}
                  </div>
                </div>
              </Cartao>
            );
          })}
        </div>
      )}

      {tipo === 'foto' && midias.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {midias.map((f) => {
            const temAlbum = Boolean(f.link);
            const capa = f.caminho ? `/api/uploads/${f.caminho}` : null;

            return (
              <figure key={f.id} className="overflow-hidden rounded-2xl bg-white shadow-cartao">
                {capa ? (
                  temAlbum ? (
                    <a href={f.link!} target="_blank" rel="noopener noreferrer nofollow" className="block">
                      <img
                        src={capa}
                        alt={f.titulo ?? 'Fotos do culto'}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover"
                      />
                    </a>
                  ) : (
                    <button onClick={() => setAbrindo(f)} className="block w-full">
                      <img
                        src={capa}
                        alt={f.titulo ?? 'Foto do culto'}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover"
                      />
                    </button>
                  )
                ) : (
                  <a
                    href={f.link ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex aspect-[4/3] w-full items-center justify-center bg-tinta-900 text-3xl text-white"
                  >
                    📁
                  </a>
                )}

                <figcaption className="p-3">
                  <p className="truncate font-semibold text-tinta-900">
                    {f.titulo ?? f.eventoTitulo ?? 'Fotos do culto'}
                  </p>
                  <p className="truncate text-xs text-tinta-400">
                    {f.eventoTitulo && f.titulo ? `${f.eventoTitulo} · ` : ''}
                    {dataCurta(f.eventoInicio ?? f.criadoEm)}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                    {temAlbum && (
                      <a
                        href={f.link!}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="rounded-lg bg-tinta-900 px-3 py-1.5 font-semibold text-white hover:bg-tinta-700"
                      >
                        Ver todas as fotos
                      </a>
                    )}
                    {capa && (
                      <a href={capa} download className="font-medium text-tinta-900 hover:underline">
                        ↓ baixar capa
                      </a>
                    )}
                    {podePublicar && (
                      <button
                        onClick={() => void apagar(f)}
                        className="ml-auto text-tinta-400 hover:text-red-600"
                      >
                        remover
                      </button>
                    )}
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}

      {abrindo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-tinta-900/80 p-4"
          onClick={() => setAbrindo(null)}
        >
          <div className="max-h-full max-w-4xl overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img
              src={`/api/uploads/${abrindo.caminho}`}
              alt={abrindo.titulo ?? 'Foto'}
              className="max-h-[80vh] w-auto rounded-2xl"
            />
            <div className="mt-3 flex items-center justify-between gap-3 text-sm text-white">
              <span>
                {abrindo.titulo ?? abrindo.eventoTitulo ?? ''} {tamanhoLegivel(abrindo.bytes)}
              </span>
              <a
                href={`/api/uploads/${abrindo.caminho}`}
                download
                className="rounded-xl bg-white px-3 py-1.5 font-semibold text-tinta-900"
              >
                ↓ Baixar foto
              </a>
            </div>
          </div>
        </div>
      )}

      {publicando && (
        <Publicar
          tipo={tipo}
          aoFechar={() => setPublicando(false)}
          aoPublicar={async (quantos) => {
            setPublicando(false);
            setAviso(
              tipo === 'video'
                ? 'Palavra publicada para os membros.'
                : quantos === 1
                  ? 'Album publicado para os membros.'
                  : `${quantos} fotos publicadas.`,
            );
            await carregar();
          }}
        />
      )}
      {dialogo}
    </Layout>
  );
}

function Publicar({
  tipo,
  aoFechar,
  aoPublicar,
}: {
  tipo: 'foto' | 'video';
  aoFechar: () => void;
  aoPublicar: (quantos: number) => Promise<void>;
}) {
  const [eventos, setEventos] = useState<{ id: number; titulo: string; inicio: string | null }[]>([]);
  const [eventoId, setEventoId] = useState('');
  const [linkVideo, setLinkVideo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState('');
  const [capa, setCapa] = useState<string | null>(null);

  useEffect(() => {
    buscarEventosParaAlbum()
      .then((d) => setEventos(d.eventos))
      .catch(() => setEventos([]));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-tinta-900/30 p-4" onClick={aoFechar}>
      <div className="my-auto w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <Cartao>
          <h3 className="text-lg font-semibold text-tinta-900">
            {tipo === 'video' ? 'Publicar uma palavra' : 'Publicar fotos do culto'}
          </h3>
          {erro && (
            <div className="mt-3">
              <Alerta>{erro}</Alerta>
            </div>
          )}

          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-medium text-tinta-700">Culto</span>
            <select
              value={eventoId}
              onChange={(e) => setEventoId(e.target.value)}
              className="w-full rounded-xl border border-tinta-200 bg-white px-3.5 py-2.5"
            >
              <option value="">sem culto (avulso)</option>
              {eventos.map((e) => (
                <option key={e.id} value={e.id}>
                  {dataCurta(e.inicio)} · {e.titulo}
                </option>
              ))}
            </select>
          </label>

          <label className="mt-3 block">
            <span className="mb-1.5 block text-sm font-medium text-tinta-700">
              {tipo === 'video' ? 'Tema da palavra' : 'Título (opcional)'}
            </span>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder={tipo === 'video' ? 'Ex.: A fe que move montanhas' : 'Ex.: Culto de domingo'}
              className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
            />
          </label>

          {tipo === 'video' && (
            <>
              <label className="mt-3 block">
                <span className="mb-1.5 block text-sm font-medium text-tinta-700">
                  Resumo (opcional)
                </span>
                <textarea
                  rows={2}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5 text-sm"
                />
              </label>

              <div className="mt-4">
                <span className="mb-1.5 block text-sm font-medium text-tinta-700">
                  Link do YouTube
                </span>
                <div className="flex gap-2">
                  <input
                    value={linkVideo}
                    onChange={(e) => setLinkVideo(e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    className="flex-1 rounded-xl border border-tinta-200 px-3.5 py-2.5"
                  />
                  <Botao
                    onClick={async () => {
                      if (!linkVideo.trim()) return;
                      setErro(null);
                      try {
                        await publicarMidia({
                          eventoId: eventoId ? Number(eventoId) : null,
                          tipo: 'video',
                          titulo: titulo.trim() || null,
                          descricao: descricao.trim() || null,
                          link: linkVideo.trim(),
                        });
                        await aoPublicar(1);
                      } catch (e) {
                        setErro(e instanceof ErroApi ? e.message : 'Não consegui publicar o link.');
                      }
                    }}
                  >
                    Publicar
                  </Botao>
                </div>
                <p className="mt-1 text-xs text-tinta-400">
                  Para pregacao inteira, o YouTube (mesmo "não listado") e o caminho — nao pesa no
                  servidor da igreja.
                </p>
              </div>
            </>
          )}

          {tipo === 'foto' && (
            <>
              <label className="mt-3 block">
                <span className="mb-1.5 block text-sm font-medium text-tinta-700">
                  Link do album (Google Drive, Google Fotos...)
                </span>
                <input
                  value={linkVideo}
                  onChange={(e) => setLinkVideo(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
                />
                <span className="mt-1 block text-xs text-tinta-400">
                  As fotos ficam no Drive; aqui entra so a capa. Lembre de deixar o album com
                  acesso "qualquer pessoa com o link".
                </span>
              </label>

              <div className="mt-4">
                <span className="mb-1.5 block text-sm font-medium text-tinta-700">
                  Foto de capa
                </span>

                {capa ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={`/api/uploads/${capa}`}
                      alt="Capa escolhida"
                      className="h-20 w-28 rounded-xl object-cover"
                    />
                    <button
                      onClick={() => setCapa(null)}
                      className="text-sm text-tinta-400 hover:text-red-600"
                    >
                      trocar
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-tinta-200 px-3 py-6 text-sm font-medium text-tinta-900 hover:border-tinta-900">
                    {enviando ? progresso || 'Enviando...' : 'Escolher a capa (até 25 MB)'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={async (e) => {
                        const arquivo = e.target.files?.[0];
                        e.target.value = '';
                        if (!arquivo) return;
                        setErro(null);
                        setEnviando(true);
                        setProgresso('Enviando a capa...');
                        try {
                          const enviado = await enviarArquivo(arquivo);
                          setCapa(enviado.caminho);
                        } catch (erroEnvio) {
                          setErro(
                            erroEnvio instanceof Error ? erroEnvio.message : 'Não consegui enviar a capa.',
                          );
                        } finally {
                          setEnviando(false);
                          setProgresso('');
                        }
                      }}
                    />
                  </label>
                )}
              </div>

              <Botao
                className="mt-4 w-full"
                disabled={!capa && !linkVideo.trim()}
                onClick={async () => {
                  setErro(null);
                  try {
                    await publicarMidia({
                      eventoId: eventoId ? Number(eventoId) : null,
                      tipo: 'foto',
                      titulo: titulo.trim() || null,
                      caminho: capa,
                      link: linkVideo.trim() || null,
                    });
                    await aoPublicar(1);
                  } catch (e) {
                    setErro(e instanceof ErroApi ? e.message : 'Não consegui publicar o album.');
                  }
                }}
              >
                Publicar album
              </Botao>

              <p className="mt-3 text-center text-xs text-tinta-400">
                Prefere subir as fotos aqui em vez do Drive? Use o campo abaixo.
              </p>
            </>
          )}

          <div className="mt-5 flex justify-end">
            <Botao variante="secundario" onClick={aoFechar}>
              Fechar
            </Botao>
          </div>
        </Cartao>
      </div>
    </div>
  );
}
